import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import {
  CircleNotch,
  Warning,
  ClipboardText,
  CheckCircle,
} from "@phosphor-icons/react";
import type { LinkedIn } from "@reactive-resume/parser";
import { LinkedInParser, PdfResumeParser } from "@reactive-resume/parser";
import type { ResumeData } from "@reactive-resume/schema";
import { dataImportTypeSchema, Pdf } from "@reactive-resume/utils";
import { motion } from "framer-motion";

import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  RichInput,
  ScrollArea,
} from "@reactive-resume/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import { useToast } from "@/client/hooks/use-toast";
import { useImportResume } from "@/client/services/resume/import";
import { useDialog } from "@/client/stores/dialog";
import { extractJobDescription } from "@/client/services/job";
import type { ResumeDto } from "@reactive-resume/dto";
import { ImportType, ValidationResult } from "@/client/pages/dashboard/resumes/_dialogs/import";
import { Textarea } from "@headlessui/react";

// LinkedIn Logo Component
const LinkedinLogo = ({ size = 20, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="currentColor"
    className={className}
    viewBox="0 0 24 24"
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const jobDescriptionFormSchema = z.object({
  jobDescription: z.string().min(1, { message: t`Job description is required` }),
});

type JobDescriptionFormValues = z.infer<typeof jobDescriptionFormSchema>;

enum ImportStep {
  IMPORT_RESUME = 0,
  JOB_DESCRIPTION = 1,
}

type Props = {
  setCurrentStep: (step: ImportStep) => void;
  uploadedFile?: File | null;
  uploadedType?: ImportType | null;
  selectedResume?: ResumeDto | null;
  validationResult: ValidationResult | null;
  setValidationResult: (result: ValidationResult | null) => void;
  isValidating: boolean;
  setIsValidating: (validating: boolean) => void;
};

export const StepJobDescription = ({
  setCurrentStep,
  uploadedFile,
  uploadedType,
  selectedResume,
  validationResult,
  setValidationResult,
  isValidating,
  setIsValidating,
}: Props) => {
  const { toast } = useToast();
  const { close } = useDialog("import");
  const { importResume, loading } = useImportResume();
  const navigate = useNavigate();

  console.log(
    "StepJobDescription - uploadedFile:",
    uploadedFile,
    "selectedResume:",
    selectedResume,
    "validationResult:",
    validationResult,
  );
  const [fetchingJobDescription, setFetchingJobDescription] = useState(false);
  const [jobUrlError, setJobUrlError] = useState<string | null>(null);
  const [jobUrl, setJobUrl] = useState<string>("");
  const [jobImportSuccess, setJobImportSuccess] = useState(false);

  const jobDescriptionForm = useForm<JobDescriptionFormValues>({
    defaultValues: {
      jobDescription: "",
    },
    resolver: zodResolver(jobDescriptionFormSchema),
  });

  const onImport = async () => {
    const jobDescription = jobDescriptionForm.getValues("jobDescription");

    console.log("Form submission - Job Description:", jobDescription);

    try {
      let data: ResumeData;
      let createdResume: ResumeDto;

      // Handle selected existing resume
      if (selectedResume) {
        console.log("Using selected resume:", selectedResume.id);
        // Use the existing resume data directly
        data = selectedResume.data as ResumeData;

        createdResume = await importResume({
          data,
          jobDescription,
          title: `${selectedResume.title} - Copy`,
        });
      }
      // Handle uploaded file
      else if (uploadedFile && uploadedType) {
        // Wait for validation to complete if still running
        if (isValidating) {
          toast({
            variant: "info",
            title: t`Processing your resume...`,
            description: t`Please wait while we finish processing your resume.`,
          });
          return;
        }

        // Check if validation was successful
        if (!validationResult || !validationResult.isValid) {
          toast({
            variant: "error",
            title: t`Resume validation failed`,
            description: t`Please try uploading your resume again.`,
          });
          return;
        }

        if (uploadedType === dataImportTypeSchema.enum["linkedin-data-export-zip"]) {
          const parser = new LinkedInParser();
          data = parser.convert(validationResult.result as LinkedIn);
        } else if (uploadedType === dataImportTypeSchema.enum["pdf-resume"]) {
          const parser = new PdfResumeParser();
          data = parser.convert(validationResult.result as ResumeData);
        } else {
          // Handle unexpected upload types
          toast({
            variant: "error",
            title: t`Unsupported file type`,
            description: t`Please upload a supported file format.`,
          });
          return;
        }

        createdResume = await importResume({
          data,
          jobDescription,
        });
      } else {
        toast({
          variant: "error",
          title: t`No resume selected`,
          description: t`Please select a resume or upload a file to continue.`,
        });
        return;
      }

      close();

      // Navigate to builder page
      navigate(`/builder/${createdResume.id}`);
    } catch (error: unknown) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const onPreviousStep = () => {
    setCurrentStep(ImportStep.IMPORT_RESUME);
  };

  const handleFetchJobDescription = async (url: string) => {
    if (!url) {
      setJobUrlError(t`Please enter a job URL`);
      return;
    }

    try {
      setJobUrlError(null);
      setFetchingJobDescription(true);
      setJobImportSuccess(false);
      const response = await extractJobDescription(url);

      if (response && response.description) {
        const description = response.description;
        jobDescriptionForm.setValue("jobDescription", description, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });

        // Show success animation
        setJobImportSuccess(true);

        // Wait for animation to complete, then proceed with import
        setTimeout(() => {
          const currentValue = jobDescriptionForm.getValues("jobDescription");
          if (currentValue !== description) {
            jobDescriptionForm.setValue("jobDescription", description, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
          }

          onImport();
        }, 2000); // 2 second delay to show the success animation
      } else {
        setJobUrlError(
          t`No job description found. Please check that you're using a valid LinkedIn job URL.`,
        );
        toast({
          variant: "error",
          title: t`Failed to import LinkedIn job`,
          description: t`No job description found for the provided LinkedIn URL.`,
        });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t`Unknown error occurred`;
      setJobUrlError(errorMessage);
      toast({
        variant: "error",
        title: t`Failed to import job description`,
        description: errorMessage,
      });
    } finally {
      setFetchingJobDescription(false);
    }
  };







  return (
    <Form {...jobDescriptionForm}>
      <form className="space-y-2" onSubmit={jobDescriptionForm.handleSubmit(onImport)}>
        {!selectedResume && validationResult && !validationResult.isValid && (
          <div className="mb-4 flex items-center space-x-2 rounded-md border border-red-200 bg-red-50 p-3">
            <Warning size={16} className="text-red-500" />
            <p className="text-sm text-red-700">{t`Resume validation failed. Please try uploading again.`}</p>
          </div>
        )}

        <p className="text-primary/70 mt-1 text-xs leading-relaxed">
          {t`Choose how you'd like to add the job description:`}
        </p>

        <div className="bg-card text-card-foreground rounded-lg border border-gray-200 p-4 shadow-sm">
          {jobImportSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 10 }}
                className="mb-4"
              >
                <CheckCircle size={64} className="text-green-500" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-2 flex items-center gap-2"
              >
                <LinkedinLogo size={24} className="text-[#0A66C2]" />
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-lg font-semibold text-green-700"
                >
                  {t`LinkedIn Job Successfully Imported!`}
                </motion.h3>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
              className="space-y-6"
            >
              {/* LinkedIn URL Import Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <LinkedinLogo size={20} className="text-[#0A66C2]" />
                  <Label className="font-inter-tight font-semibold text-[#0A66C2]">{t`Import from LinkedIn Job URL`}</Label>
                </div>
                <div className="flex space-x-2">
                  <Input
                    placeholder={t`https://www.linkedin.com/jobs/view/4012345678`}
                    value={jobUrl}
                    onChange={(e) => {
                      setJobUrl(e.target.value);
                      setJobUrlError(null);
                      setJobImportSuccess(false);
                    }}
                    className="border-[#0A66C2]/20 focus:border-[#0A66C2] focus:ring-[#0A66C2]/20"
                  />
                  <Button
                    type="button"
                    onClick={() => handleFetchJobDescription(jobUrl)}
                    disabled={!jobUrl || fetchingJobDescription}
                    className="bg-[#0A66C2] whitespace-nowrap text-white hover:bg-[#004182]"
                  >
                    {fetchingJobDescription ? (
                      <CircleNotch size={16} className="mr-2 animate-spin" />
                    ) : (
                      <LinkedinLogo size={16} className="mr-2" />
                    )}
                    {t`Import`}
                  </Button>
                </div>
                <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                  <p className="text-xs leading-relaxed font-medium text-[#0A66C2]">
                    {t`Copy the job URL from LinkedIn and paste it above to automatically import the job description.`}
                  </p>
                </div>
                {jobUrlError && (
                  <div className="text-error flex items-start space-x-2 text-sm">
                    <Warning size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{jobUrlError}</span>
                  </div>
                )}
              </div>

              {/* OR Divider */}
              <div className="flex items-center">
                <div className="flex-1 border-t border-gray-300"></div>
                <div className="px-4 text-sm font-medium text-gray-500 bg-white">{t`OR`}</div>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* Manual Job Description Section */}
              <div className="space-y-3">
                <FormField
                  name="jobDescription"
                  control={jobDescriptionForm.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t`Enter Job Description Manually`}</FormLabel>
                      <FormDescription>
                        {t`Paste or type the job description that you want to apply for.`}
                      </FormDescription>
                      <FormControl>
                        <textarea
                          className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[200px] w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                          key={`rich-input-${field.value.length}`}
                          onChange={(e) => {
                            const value = e.target.value;
                            console.log("RichInput onChange called with:", value);
                            field.onChange(value);
                            jobDescriptionForm.setValue("jobDescription", value, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }}
                          placeholder={t`Paste or type the job description here...`}
                          value={field.value}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </motion.div>
          )}
        </div>

        <DialogFooter className="flex w-full justify-between! space-y-4 border-t border-gray-200 pt-4">
          <Button type="button" variant="ghost" onClick={onPreviousStep}>
            {t`Back`}
          </Button>
          <Button
            type="submit"
            disabled={loading || (!selectedResume && isValidating && !validationResult)}
          >
            {loading ? (
              <CircleNotch size={16} className="mr-2 animate-spin" />
            ) : !selectedResume && isValidating ? (
              <CircleNotch size={16} className="mr-2 animate-spin" />
            ) : null}
            {!selectedResume && isValidating ? t`Processing...` : t`Import & Optimize`}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
